/**
 * Parses an API value (string or number) to a number.
 * Returns null for empty/invalid values.
 *
 * @param apiValue - The value from the API (e.g., "1234.5000", 1234.5, or null)
 * @returns The parsed number, or null if empty/invalid
 *
 * @example
 * parseApiNumber("1234.5000") // 1234.5
 * parseApiNumber("0.00") // 0
 * parseApiNumber(1234.5) // 1234.5
 * parseApiNumber(null) // null
 * parseApiNumber("") // null
 */
export function parseApiNumber(
  apiValue: string | number | null | undefined,
): number | null {
  if (apiValue === null || apiValue === undefined || apiValue === '') {
    return null;
  }

  if (typeof apiValue === 'number') {
    return Number.isNaN(apiValue) ? null : apiValue;
  }

  const numericValue = Number(apiValue);

  return Number.isNaN(numericValue) ? null : numericValue;
}

/**
 * Parses an API value (string or number) to an integer.
 * Returns null for empty/invalid values.
 *
 * @param apiValue - The value from the API (e.g., "1", 1, or null)
 * @returns The parsed integer, or null if empty/invalid
 *
 * @example
 * parseApiInteger("1") // 1
 * parseApiInteger(1) // 1
 * parseApiInteger("1.5") // 2 (rounded)
 * parseApiInteger(null) // null
 */
export function parseApiInteger(
  apiValue: string | number | null | undefined,
): number | null {
  if (apiValue === null || apiValue === undefined || apiValue === '') {
    return null;
  }

  const num = typeof apiValue === 'string' ? Number(apiValue) : apiValue;
  return Number.isNaN(num) ? null : Math.round(num);
}

/**
 * Check if a string represents an integer.
 *
 * @param string - The string to check.
 * @returns True if the string represents an integer, false otherwise.
 */
export const isInteger = (string: string) => {
  /**
   * Regular expression to match strings that represent integers.
   * The regular expression matches strings that start with a digit
   * other than zero, followed by zero or more digits.
   * @type {RegExp}
   */
  const integerRegex = /^[1-9]\d*$/;

  // Check if the string matches the integer regex.
  return integerRegex.test(string);
};

/**
 * Check if a value is a valid number.
 *
 * @param value - The value to check (string or number).
 * @returns True if the value is a valid finite number, false otherwise.
 *
 * @example
 * isValidNumber(123)      // returns true
 * isValidNumber('123')     // returns true
 * isValidNumber(NaN)       // returns false
 * isValidNumber('abc')     // returns false
 * isValidNumber(Infinity)  // returns false
 * isValidNumber(1.234)     // returns true
 */
export const isValidNumber = (value: string | number | undefined | null) => {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }

  if (typeof value === 'string') {
    // Empty or whitespace-only strings are not valid numbers
    if (value.trim() === '') {
      return false;
    }

    // Use Number() instead of parseFloat() for stricter validation
    // Number() requires the entire string to be a valid number
    // parseFloat() does partial parsing (e.g., '12abc' -> 12)
    const parsed = Number(value);
    return Number.isFinite(parsed);
  }

  return false;
};

/**
 * Calculate the precision (number of decimal places) of a number.
 * Uses mathematical approach for accuracy with floating-point numbers.
 *
 * @param number - The input number to calculate the precision from.
 * @returns The precision of the number (number of decimal places).
 *
 * @example
 * precision(1.23) // returns 2
 * precision(1.0)  // returns 0
 * precision(1.234) // returns 3
 * precision(0.1 + 0.2) // returns 1 (handles floating-point correctly)
 * precision(null) // returns 0
 * precision(undefined) // returns 0
 */
export const calculatePrecisionOfNumber = (
  number: number | undefined | null,
) => {
  // Handle null, undefined, and non-finite numbers
  if (!isValidNumber(number)) {
    return 0;
  }

  // Start with multiplier 1 and 0 decimalPlaces.
  let multiplier = 1;
  let decimalPlaces = 0;

  // Keep multiplying by 10 until we get a whole number
  // For example, with 1.23:
  // 1st loop: 1.23 * 1 = 1.23    (rounded != original)
  // 2nd loop: 1.23 * 10 = 12.3   (rounded != original)
  // 3rd loop: 1.23 * 100 = 123   (rounded == original, done!)
  while (Math.round(number * multiplier) / multiplier !== number) {
    // Safety check to prevent infinite loops with very small numbers
    if (decimalPlaces >= 10) {
      break;
    }

    multiplier *= 10;
    decimalPlaces++;
  }

  return decimalPlaces;
};
